import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "./supabase";

export type AuthStatus = "lädt" | "angemeldet" | "abgemeldet" | "ohne-backend";

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
}

/**
 * Sitzung von Supabase. Ist keine Instanz konfiguriert, läuft die App im
 * lokalen Modus weiter ("ohne-backend"), statt beim Start zu scheitern.
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(() =>
    getSupabase() ? { status: "lädt", session: null } : { status: "ohne-backend", session: null },
  );

  useEffect(() => {
    const client = getSupabase();
    if (!client) return;

    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setState({ status: data.session ? "angemeldet" : "abgemeldet", session: data.session });
    });

    const { data: listener } = client.auth.onAuthStateChange((_event, session) => {
      setState({ status: session ? "angemeldet" : "abgemeldet", session });
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  return state;
}

/**
 * Anmeldung über Apple oder Google. Ein Einladungscode reist in den
 * Nutzer-Metadaten mit, damit der Trigger beim ersten Login das Mitglied
 * anlegen kann (siehe docs/auth-setup.md).
 *
 * Der Einladungscode kann bei OAuth nicht in den Nutzer-Metadaten mitreisen,
 * diese Fassung von supabase-js kennt dafür kein Feld. Er wird deshalb lokal
 * gemerkt und nach der Rückkehr eingelöst (siehe pendingInvite).
 *
 * Bewusst **ohne** `redirectTo`: unsere GoTrue-Fassung (v2.186) hängt diesen
 * Wert unverändert an die Google-URL. Google schleppt ihn doppelt kodiert
 * durch die Kontoauswahl und bricht danach mit einem nackten 400 ab
 * ("malformed"). Ohne den Parameter kehrt die Anmeldung zur SITE_URL der
 * Instanz zurück, und das ist genau diese App.
 *
 * Nebenwirkung für die Entwicklung: eine Anmeldung von localhost landet
 * danach auf der Live-App, nicht auf dem Entwicklungsserver.
 */
export async function signIn(provider: "apple" | "google", inviteCode?: string): Promise<void> {
  const client = getSupabase();
  if (!client) throw new Error("Keine Supabase-Instanz konfiguriert");

  if (inviteCode) rememberInvite(inviteCode);

  const { error } = await client.auth.signInWithOAuth({ provider });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  const client = getSupabase();
  if (!client) return;
  await client.auth.signOut();
}

/**
 * Einladungscode über den Umweg zu Google hinweg merken. Er wird nach der
 * Anmeldung eingelöst und danach gelöscht.
 */
const INVITE_KEY = "ainkauf.invite";

export function rememberInvite(code: string): void {
  globalThis.localStorage?.setItem(INVITE_KEY, code.trim().toUpperCase());
}

export function pendingInvite(): string | null {
  return globalThis.localStorage?.getItem(INVITE_KEY) ?? null;
}

export function clearInvite(): void {
  globalThis.localStorage?.removeItem(INVITE_KEY);
}
